import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Grid, Card, CardContent,
  TextField, LinearProgress, Alert, Avatar, IconButton, Tooltip,
  InputAdornment, MenuItem, Tabs, Tab, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, List, ListItem, ListItemText,
} from '@mui/material';
import {
  Search, People, Email, Phone, LinkedIn, Close,
  AccountTree, FilterList,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const AVATAR_COLORS = [
  '#1a237e','#b71c1c','#1b5e20','#e65100','#4a148c',
  '#006064','#880e4f','#37474f','#bf360c','#1565c0',
];
function avatarColor(name: string) {
  const idx = (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}
function initials(firstName?: string, lastName?: string) {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

interface Employee { id: string; employeeId: string; firstName: string; lastName: string; email?: string; phone?: string; department?: string; jobTitle?: string; employmentType?: string; status?: string; startDate?: string; managerId?: string; officeLocation?: string; workPhone?: string; linkedinUrl?: string; bio?: string; skills?: string[]; }

function EmployeeCard({ emp, onClick }: { emp: Employee; onClick: () => void }) {
  const name = `${emp.firstName} ${emp.lastName}`;
  const color = avatarColor(name);
  return (
    <Card sx={{ cursor: 'pointer', transition: 'all 0.15s', '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' } }} onClick={onClick}>
      <CardContent sx={{ textAlign: 'center', pb: '12px !important' }}>
        <Avatar sx={{ width: 64, height: 64, bgcolor: color, fontSize: '1.4rem', fontWeight: 700, mx: 'auto', mb: 1 }}>
          {initials(emp.firstName, emp.lastName)}
        </Avatar>
        <Typography fontWeight={700} variant="body1" noWrap>{name}</Typography>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.5 }}>{emp.jobTitle ?? '—'}</Typography>
        <Chip label={emp.department ?? 'No Dept'} size="small" variant="outlined" sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          {emp.email && (
            <Tooltip title={emp.email}>
              <IconButton size="small" onClick={e => { e.stopPropagation(); window.location.href = `mailto:${emp.email}`; }}>
                <Email fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {emp.phone && (
            <Tooltip title={emp.phone}>
              <IconButton size="small" onClick={e => { e.stopPropagation(); window.location.href = `tel:${emp.phone}`; }}>
                <Phone fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function EmployeeDirectory() {
  const { user } = useAuth();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filtered, setFiltered] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEmp, setSelectedEmp] = useState<Employee | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await apiFetch('/api/employees?limit=500&status=active');
      const emps: Employee[] = data.data || data || [];
      setEmployees(emps);

      const depts = Array.from(new Set(emps.map(e => e.department).filter(Boolean))) as string[];
      setDepartments(depts.sort());
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let result = employees;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.jobTitle?.toLowerCase().includes(q) ||
        e.department?.toLowerCase().includes(q) ||
        e.employeeId?.toLowerCase().includes(q)
      );
    }
    if (deptFilter) result = result.filter(e => e.department === deptFilter);
    setFiltered(result);
  }, [employees, search, deptFilter]);

  // Group by department for list view
  const byDept = filtered.reduce((acc: Record<string, Employee[]>, e) => {
    const d = e.department ?? 'Other';
    acc[d] = [...(acc[d] ?? []), e];
    return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <People sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Employee Directory</Typography>
            <Typography variant="body2" color="text.secondary">{filtered.length} of {employees.length} employees</Typography>
          </Box>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Search + Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search name, title, department, email…"
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
          <TextField label="Department" select size="small" sx={{ minWidth: 180 }} value={deptFilter}
            onChange={e => setDeptFilter(e.target.value)}>
            <MenuItem value="">All Departments</MenuItem>
            {departments.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant={viewMode === 'grid' ? 'contained' : 'outlined'} size="small"
              onClick={() => setViewMode('grid')} sx={{ minWidth: 40 }}>⊞</Button>
            <Button variant={viewMode === 'list' ? 'contained' : 'outlined'} size="small"
              onClick={() => setViewMode('list')} sx={{ minWidth: 40 }}>☰</Button>
          </Box>
        </Box>

        {/* Department chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          <Chip label="All" size="small" onClick={() => setDeptFilter('')}
            color={!deptFilter ? 'primary' : 'default'} variant={!deptFilter ? 'filled' : 'outlined'} />
          {departments.map(d => (
            <Chip key={d} label={`${d} (${employees.filter(e => e.department === d).length})`} size="small"
              onClick={() => setDeptFilter(d === deptFilter ? '' : d)}
              color={deptFilter === d ? 'primary' : 'default'} variant={deptFilter === d ? 'filled' : 'outlined'} />
          ))}
        </Box>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── GRID VIEW ── */}
      {viewMode === 'grid' && (
        <Grid container spacing={2}>
          {filtered.map(e => (
            <Grid key={e.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <EmployeeCard emp={e} onClick={() => setSelectedEmp(e)} />
            </Grid>
          ))}
          {filtered.length === 0 && !loading && (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                <People sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography>No employees found</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* ── LIST VIEW (grouped by dept) ── */}
      {viewMode === 'list' && (
        <Box>
          {Object.entries(byDept).sort(([a], [b]) => a.localeCompare(b)).map(([dept, emps]) => (
            <Box key={dept} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccountTree sx={{ fontSize: 18, color: '#1a237e' }} />
                <Typography fontWeight={700} color="primary">{dept}</Typography>
                <Chip label={emps.length} size="small" />
              </Box>
              <Paper variant="outlined">
                {emps.map((emp, i) => {
                  const name = `${emp.firstName} ${emp.lastName}`;
                  const color = avatarColor(name);
                  return (
                    <Box key={emp.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, cursor: 'pointer', borderTop: i > 0 ? '1px solid #f0f0f0' : 'none', '&:hover': { bgcolor: '#f5f5f5' } }}
                      onClick={() => setSelectedEmp(emp)}>
                      <Avatar sx={{ width: 38, height: 38, bgcolor: color, fontSize: '0.85rem', fontWeight: 700, flexShrink: 0 }}>
                        {initials(emp.firstName, emp.lastName)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={700} noWrap>{name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{emp.jobTitle ?? '—'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                        {emp.email && <Typography variant="caption" color="text.secondary">{emp.email}</Typography>}
                        {emp.employmentType && <Chip label={emp.employmentType} size="small" variant="outlined" />}
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            </Box>
          ))}
          {filtered.length === 0 && !loading && (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No employees found.</Paper>
          )}
        </Box>
      )}

      {/* ── PROFILE DIALOG ── */}
      {selectedEmp && (
        <Dialog open maxWidth="sm" fullWidth onClose={() => setSelectedEmp(null)}>
          <DialogTitle>
            <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setSelectedEmp(null)}><Close /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Avatar sx={{
                width: 80, height: 80, fontSize: '2rem', fontWeight: 700, mx: 'auto', mb: 1.5,
                bgcolor: avatarColor(`${selectedEmp.firstName} ${selectedEmp.lastName}`),
              }}>
                {initials(selectedEmp.firstName, selectedEmp.lastName)}
              </Avatar>
              <Typography variant="h6" fontWeight={700}>{selectedEmp.firstName} {selectedEmp.lastName}</Typography>
              <Typography color="text.secondary">{selectedEmp.jobTitle ?? '—'}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                {selectedEmp.department && <Chip label={selectedEmp.department} size="small" color="primary" variant="outlined" />}
                {selectedEmp.employmentType && <Chip label={selectedEmp.employmentType} size="small" variant="outlined" />}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Employee ID</Typography>
                <Typography variant="body2" fontWeight={600}>{selectedEmp.employeeId}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Start Date</Typography>
                <Typography variant="body2">{selectedEmp.startDate ? new Date(selectedEmp.startDate + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</Typography>
              </Grid>
              {selectedEmp.email && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="body2">
                      <a href={`mailto:${selectedEmp.email}`} style={{ color: '#1a237e' }}>{selectedEmp.email}</a>
                    </Typography>
                  </Box>
                </Grid>
              )}
              {(selectedEmp.phone || selectedEmp.workPhone) && (
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2">{selectedEmp.phone || selectedEmp.workPhone}</Typography>
                  </Box>
                </Grid>
              )}
              {selectedEmp.officeLocation && (
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Office</Typography>
                  <Typography variant="body2">{selectedEmp.officeLocation}</Typography>
                </Grid>
              )}
            </Grid>

            {selectedEmp.bio && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">About</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>{selectedEmp.bio}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {selectedEmp.email && (
              <Button startIcon={<Email />} href={`mailto:${selectedEmp.email}`}>Email</Button>
            )}
            <Button onClick={() => setSelectedEmp(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
